// A library change owns one continuation through data hydration or recovery.
// The journal contains IDs only; drafts and queued writes are never cleared.
export function createLibraryTransition(deps) {
    let active = null;
    const journalKey = () => 'tsLibraryTransition:' + deps.subject();
    const failed = error => deps.deferred().reject(error).promise();
    const attempt = callback => { try { return callback(); } catch (error) { return failed(error); } };
    function change(target) {
        if (active) return active;
        const dfd = deps.deferred();
        active = dfd.promise();
        const result = active;
        const key = journalKey();
        const previous = deps.current();
        let owner;
        const current = () => deps.isCurrent(owner);
        let journal, resuming = false;
        try {
            owner = deps.fence();
            journal = JSON.parse(deps.storage.getItem(key) || 'null');
            if (journal && ![journal.previous, journal.target].every(value => Number.isSafeInteger(value) && value > 0 && value <= 2147483647)) throw new Error('Invalid library recovery');
            resuming = !!journal;
            journal = journal || { previous, target };
            deps.storage.setItem(key, JSON.stringify(journal));
        } catch (error) {
            active = null;
            deps.abandon();
            dfd.reject({ libraryUnconfirmed: true, cause: error });
            return result;
        }
        deps.busy(true);
        const finish = (error, recovered) => {
            if (!current()) { active = null; deps.abandon(); return dfd.reject({ cancelled: true }); }
            if (!error || recovered) deps.storage.removeItem(key);
            deps.busy(false, !error || recovered);
            active = null;
            if (error) dfd.reject({ ...error, libraryRecovered: recovered, libraryUnconfirmed: !recovered });
            else dfd.resolve({ library: target });
        };
        const recover = error => {
            if (error?.cancelled) return finish(error, false);
            if (!current()) return finish({ cancelled: true }, false);
            // First reconcile an ambiguous transport result with the authoritative
            // reader. Do not replay an uncertain write or infer its outcome.
            attempt(() => deps.context()).then(() => {
                if (!current()) return finish({ cancelled: true }, false);
                const effective = deps.current();
                const restore = effective === journal.previous ? deps.resolved() : attempt(() => deps.request(journal.previous, effective));
                restore.then(() => current() ? attempt(() => deps.load(journal.previous)) : failed({ cancelled: true }))
                    .then(() => finish(error, true)).fail(() => finish(error, false));
            }).fail(() => finish(error, false));
        };
        const dispatch = () => {
            attempt(() => deps.request(target, previous)).then(() => {
                if (!current()) return failed({ cancelled: true });
                return attempt(() => deps.load(target));
            }).then(() => finish(null, true)).fail(recover);
        };
        if (resuming) {
            // A saved journal means the prior write's outcome is unknown even
            // when its target still matches the restored local snapshot.
            attempt(() => deps.context()).then(() => {
                if (!current()) return finish({ cancelled: true }, false);
                const effective = deps.current();
                if (effective === target) {
                    // The original POST already committed. Hydrate its result;
                    // never replay it or restore the stale previous authority.
                    attempt(() => deps.load(target)).then(() => finish(null, true)).fail(error => finish(error, false));
                } else if (effective === journal.previous || effective === journal.target) {
                    attempt(() => {
                        journal = { previous: effective, target };
                        deps.storage.setItem(key, JSON.stringify(journal));
                        return deps.request(target, effective);
                    }).then(() => {
                        if (!current()) return failed({ cancelled: true });
                        return attempt(() => deps.load(target));
                    }).then(() => finish(null, true)).fail(recover);
                } else {
                    // Another context won while offline. Adopt that fresh
                    // authority rather than rolling it back from the journal.
                    attempt(() => deps.load(effective)).then(() => finish({ libraryContextChanged: true }, true)).fail(error => finish(error, false));
                }
            }).fail(error => finish(error, false));
        } else dispatch();
        return result;
    }
    return { change, pending: () => !!active || !!deps.storage.getItem(journalKey()), target: () => {
        try { return JSON.parse(deps.storage.getItem(journalKey()) || 'null')?.target; } catch { return undefined; }
    } };
}
